import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";

interface PartialPaymentReceiptData {
  restaurantName: string;
  tableNumber?: string;
  tabNumber?: string;
  commandNumber?: string;
  payerName?: string;
  paymentMethodName: string;
  amount: number;
  orderNumber: number;
  createdAt: Date;
}

export const formatPartialPaymentReceipt = (
  data: PartialPaymentReceiptData,
  charsPerLine: number = 48
): string[] => {
  const lines: string[] = [];
  const separator = "=".repeat(charsPerLine);
  const thinSeparator = "-".repeat(charsPerLine);

  const center = (text: string): string => {
    const padding = Math.max(0, Math.floor((charsPerLine - text.length) / 2));
    return " ".repeat(padding) + text;
  };

  const formatCurrency = (value: number): string => {
    return `R$ ${value.toFixed(2).replace(".", ",")}`;
  };

  // Header
  lines.push(separator);
  lines.push(center("PAGAMENTO PARCIAL"));
  lines.push(separator);

  // Location info
  if (data.tableNumber) {
    lines.push(`Mesa: ${data.tableNumber}`);
  }
  if (data.tabNumber) {
    lines.push(`Comanda: ${data.tabNumber}`);
  }
  if (data.commandNumber) {
    lines.push(`Comanda: ${data.commandNumber}`);
  }
  
  lines.push(`Pedido: #${data.orderNumber}`);
  lines.push(
    `Data: ${format(data.createdAt, "dd/MM/yyyy HH:mm", { locale: ptBR })}`
  );
  lines.push("");

  // Payment info
  if (data.payerName) {
    lines.push(`Pagante: ${data.payerName}`);
  }
  lines.push(`Metodo: ${data.paymentMethodName}`);
  lines.push(`Valor: ${formatCurrency(data.amount)}`);
  lines.push("");

  // Footer
  lines.push(thinSeparator);
  lines.push(center("Este pagamento sera abatido"));
  lines.push(center("do total da conta."));
  lines.push(thinSeparator);
  lines.push("");
  lines.push(center(data.restaurantName));
  lines.push(separator);
  lines.push("");
  lines.push("");
  lines.push("");

  return lines;
};

export const printPartialPaymentReceipt = async (
  data: PartialPaymentReceiptData,
  printerName: string,
  charsPerLine: number = 48
): Promise<void> => {
  const lines = formatPartialPaymentReceipt(data, charsPerLine);
  
  // Use the existing QZ Tray print mechanism
  const { printReceipt } = await import("./qz-tray");
  await printReceipt(printerName, lines.join("\n"), {
    skipLog: false,
    logType: "pagamento-parcial",
    logIdentifier: data.orderNumber,
  });
};

/**
 * Get a printer configured for partial_payment job type
 */
export const getPrinterForPartialPayment = async (
  restaurantId: string
): Promise<{ printerName: string; charsPerLine: number } | null> => {
  try {
    // Try to get printer configured for partial_payment job type
    const { data: jobTypeData } = await supabase
      .from("printer_job_types")
      .select(`
        printer_id,
        printers!inner (
          id,
          printer_name,
          is_active,
          restaurant_id,
          chars_per_line
        )
      `)
      .eq("job_type", "partial_payment")
      .eq("is_enabled", true);

    // Filter by restaurant and active status
    const validPrinters = jobTypeData?.filter(
      (jt: any) => 
        jt.printers?.restaurant_id === restaurantId && 
        jt.printers?.is_active === true
    );

    if (validPrinters && validPrinters.length > 0) {
      const printer = validPrinters[0].printers;
      return {
        printerName: printer.printer_name,
        charsPerLine: printer.chars_per_line || 48,
      };
    }

    return null;
  } catch (error) {
    console.error("Error getting printer for partial payment:", error);
    return null;
  }
};
